import { useEffect, useRef } from "react";
import { Bell, Check, CheckCheck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useNotifications,
  useMarkRead,
  useMarkAllRead,
  type AppNotification,
} from "@/hooks/useNotifications";

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });
}

interface Props {
  onClose: () => void;
}

export const NotificationPanel = ({ onClose }: Props) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications(1);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();

  const notifications = data?.data ?? [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const handleItem = (n: AppNotification) => {
    if (!n.isRead) markRead.mutate(n.id);
    onClose();
    navigate("/notifications");
  };

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-card shadow-lg z-50 overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">
            Notifications
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            title="Mark all as read"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            All read
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {isLoading && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        )}
        {!isLoading && notifications.length === 0 && (
          <div className="py-10 text-center">
            <Bell className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No notifications yet
            </p>
          </div>
        )}
        {notifications.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => handleItem(n)}
            className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-accent/50 transition-colors ${
              !n.isRead ? "bg-primary/5" : ""
            }`}
          >
            <div className="flex items-start gap-2">
              <div
                className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                  n.isRead ? "bg-transparent" : "bg-primary"
                }`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {n.title}
                </p>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                  {n.body}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {relativeTime(n.sentAt)}
                </p>
              </div>
              {n.isRead && (
                <Check className="w-3.5 h-3.5 text-muted-foreground/40 mt-1 flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="px-4 py-2.5 border-t border-border">
        <button
          type="button"
          onClick={() => {
            onClose();
            navigate("/notifications");
          }}
          className="text-xs text-primary hover:underline"
        >
          See all notifications →
        </button>
      </div>
    </div>
  );
};
